import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CustomDialog,
  CustomList,
  StyledDataGrid,
} from "../../../components/StyledComponents";
import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import CustomGridToolbarQuickFilter from "../CustomGridToolbarQuickFilter";
import EditNew20pxSvg from "../../../assets/images/icons/EditNew20pxSvg";
import DeleteIcon16pxSvg from "../../../assets/images/icons/DeleteIcon16pxSvg";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import AddNewLeaveTypeDialog from "./AddNewLeaveTypeDialog";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { toast } from "react-toastify";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import EditLeaveTypeDialog from "./EditLeaveTypeDialog";
import DeleteLeaveTypeDialog from "./DeleteLeaveTypeDialog";
import { format } from "date-fns";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const ConfigureLeaveTypesDialog = (props) => {
  const { t } = useTranslation();
  const { getLeaveTypeData, restoreLeaveType } = LeaveApi();

  // nhận từ props
  const { dateTimeFormat, genaralSettingDateFormat, onRefresh } = props;

  const [leaveTypesData, setLeaveTypesData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openEditType, setOpenEditType] = useState(false);
  const [openDeleteType, setOpenDeleteType] = useState(false);
  const [openAddNewType, setOpenAddNewType] = useState(false);

  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);

  const combinedDateTimeFormat = useMemo(() => {
    const dateFmt = (genaralSettingDateFormat || "dd/MM/yyyy").trim();
    const timeFmt = (dateTimeFormat || "HH:mm:ss").trim();

    // timeFmt có chứa phần ngày (y/M/d) => coi là full datetime, dùng luôn
    const timeHasDateToken =
      timeFmt.includes("y") || timeFmt.includes("M") || timeFmt.includes("d");

    if (timeHasDateToken) {
      return timeFmt;
    }

    // time chỉ có giờ => ghép "ngày giờ"
    if (dateFmt && timeFmt) {
      return `${dateFmt} ${timeFmt}`;
    }
    return dateFmt || timeFmt;
  }, [genaralSettingDateFormat, dateTimeFormat]);


  const handleCloseEditType = (isSaved = false) => {
    setOpenEditType(false);
    if (isSaved) fetchLeaveTypeData(checkedItems.showDeletedLeaveTypes);
  };

  // isDeleted=true → loại phép đang dùng; isDeleted=false → loại phép đã xóa
  const fetchLeaveTypeData = async (showDeleted = false) => {
    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      isDeleted: !showDeleted,
    };
    try {
      const response = await getLeaveTypeData(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveTypesData(response.data.data ?? []);
      }
    } catch (error) {
      if (error?.response) {
        if (error.response.status !== 401) {
          toast.error(error?.response?.data?.message || "Error");
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      console.error("Error fetching leave types:", error);
    }
  };

  const [checkedItems, setCheckedItems] = useState({
    showDeletedLeaveTypes: false,
  });

  useEffect(() => {
    if (!props.open) {
      setSelectedRow(null);
      setSelectionModel([]);
      setCheckedItems({ showDeletedLeaveTypes: false });
      return;
    }
    fetchLeaveTypeData(false);
  }, [props.open]);

  const handleToggleShowDeleted = useCallback(() => {
    const newValue = !checkedItems.showDeletedLeaveTypes;
    setCheckedItems({ showDeletedLeaveTypes: newValue });
    fetchLeaveTypeData(newValue);
    setSelectedRow(null);
    setSelectionModel([]);
  }, [checkedItems.showDeletedLeaveTypes]);

  useEffect(() => {
    const mapped = (leaveTypesData || []).map((leaveType, idx) => ({
      id: leaveType.leaveTypeId ?? idx + 1,
      ...leaveType,
    }));

    setRowData(mapped);
  }, [leaveTypesData, checkedItems.showDeletedLeaveTypes, combinedDateTimeFormat]);

  const safeFormatDate = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const v =
      typeof value === "string" && value.includes(" ")
        ? value.replace(" ", "T")
        : value;
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(value);
    try {
      return format(d, combinedDateTimeFormat);
    } catch {
      return String(value);
    }
  };

  const renderBooleanIcon = (value) => {
    if (value === true) {
      return <CheckCircleIcon sx={{ color: "#16A34A" }} />; // green-600
    }
    if (value === false) {
      return <CancelIcon sx={{ color: "#DC2626" }} />; // red-600
    }
    return "-";
  };

  // Chuẩn hóa hoursPerLeaveDay (string hoặc object LocalTime) để hiển thị
  const formatHoursPerLeaveDay = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const hour = String(value.hour ?? 0).padStart(2, "0");
      const minute = String(value.minute ?? 0).padStart(2, "0");
      const second = String(value.second ?? 0).padStart(2, "0");
      return `${hour}:${minute}:${second}`;
    }
    return String(value);
  };

  const columnData = useMemo(
    () => [
      {
        field: "leaveTypeCode",
        headerName: t("leave.code"),
        minWidth: 140,
        flex: 0.8,
      },
      {
        field: "remark",
        headerName: t("leave.remarkVI"),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "remarkEN",
        headerName: t("leave.remarkEN"),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "entitlement",
        headerName: t("leave.max-leave-days"),
        minWidth: 150,
        flex: 0.8,
      },
      {
        field: "maxLeaveDuration",
        headerName: t("leave.max-leave-duration"),
        minWidth: 150,
        flex: 0.8,
        valueGetter: (value) =>
          value === null || value === undefined || value === "" ? "-" : value,
      },
      {
        field: "isMonthlyLimit",
        headerName: t("leave.monthly-limit"),
        minWidth: 140,
        flex: 0.7,
        renderCell: (params) => renderBooleanIcon(params.value),
      },
      {
        field: "monthlyLimitDays",
        headerName: t("leave.monthly-limit-days"),
        minWidth: 120,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.isMonthlyLimit ? value ?? "-" : "-",
      },
      {
        field: "isWeeklyLimit",
        headerName: t("leave.weekly-limit"),
        minWidth: 140,
        flex: 0.7,
        renderCell: (params) => renderBooleanIcon(params.value),
      },
      {
        field: "weeklyLimitDays",
        headerName: t("leave.weekly-limit-days"),
        minWidth: 120,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.isWeeklyLimit ? value ?? "-" : "-",
      },
      {
        field: "hoursPerLeaveDay",
        headerName: t("common.hoursperleaveday"),
        minWidth: 140,
        flex: 0.7,
        valueGetter: (value) => formatHoursPerLeaveDay(value),
      },
      {
        field: "isMonthlyAccrual",
        headerName: t("leave.monthly-accrual"),
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => renderBooleanIcon(params.value),
      },
      {
        field: "monthlyAccrualDay",
        headerName: t("leave.accrue-monthly"),
        minWidth: 120,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.isMonthlyAccrual ? value ?? "-" : "-",
      },
      {
        field: "isCarry",
        headerName: t("leave.allow-carryover"),
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => renderBooleanIcon(params.value),
      },
      {
        field: "maxCarry",
        headerName: t("leave.max-carryover-days"),
        minWidth: 120,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.isCarry ? value ?? "-" : "-",
      },
      {
        field: "carryExpireMonth",
        headerName: t("leave.carryover-expire-month"),
        minWidth: 120,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.isCarry ? value ?? "-" : "-",
      },
      {
        field: "description",
        headerName: t("leave.leave-type-description"),
        minWidth: 180,
        flex: 1.2,
      },
    ],
    [t]
  );

  const handleOpenAddNewType = () => setOpenAddNewType(true);
  const handleCloseAddNewType = () => setOpenAddNewType(false);

  const isViewingDeleted = checkedItems.showDeletedLeaveTypes;

  const handleEditRow = useCallback((row) => {
    if (!row) {
      toast.warn(t("common.select-a-row-first"));
      return;
    }
    setSelectedRow(row);
    setOpenEditType(true);
  }, [t]);

  const handleDeleteRow = useCallback((row) => {
    if (!row) {
      toast.warn(t("common.select-a-row-first"));
      return;
    }
    setSelectedRow(row);
    setOpenDeleteType(true);
  }, [t]);

  const handleRestore = useCallback(async (row) => {
    const target = row || selectedRow;
    if (!target?.leaveTypeId) {
      toast.warn(t("common.select-a-row-first"));
      return;
    }
    try {
      const payload = {
        langId: localStorage.getItem("i18nextLngId"),
        leaveTypeId: target.leaveTypeId,
      };
      const res = await restoreLeaveType(payload);
      if (res?.status === 200) {
        toast.success(t("common.restore-success"));
        setSelectedRow(null);
        fetchLeaveTypeData(true); // vẫn đang ở chế độ xem deleted
        onRefresh();
      } else {
        toast.error(res?.data?.message || t("common.error"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t("common.network-or-server-error"));
      console.error("restoreLeaveType error:", e);
    }
  }, [selectedRow, t, onRefresh]);

  const optionsColumn = useMemo(
    () => ({
      field: "option",
      headerName: t("common.options"),
      width: 120,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const row = params.row;
        const isDefaultLeaveType = Number(row?.leaveTypeId) === 1;
        const iconBtnClass =
          "inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg hover:bg-gray-100 transition cursor-pointer p-0";

        if (isViewingDeleted) {
          return (
            <div
              className="flex flex-nowrap items-center justify-start gap-1 h-full w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title={t("button.restore")} arrow>
                <button
                  type="button"
                  className={iconBtnClass}
                  onClick={() => handleRestore(row)}
                >
                  <RefreshCircleIcon20pxSvgV2 />
                </button>
              </Tooltip>
            </div>
          );
        }

        if (isDefaultLeaveType) return null;

        return (
          <div
            className="flex flex-nowrap items-center justify-start gap-1 h-full w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={t("button.edit")} arrow>
              <button
                type="button"
                className={iconBtnClass}
                onClick={() => handleEditRow(row)}
              >
                <EditNew20pxSvg width={20} height={20} />
              </button>
            </Tooltip>
            <Tooltip title={t("button.delete")} arrow>
              <button
                type="button"
                className={`${iconBtnClass} hover:bg-red-100`}
                onClick={() => handleDeleteRow(row)}
              >
                <DeleteIcon16pxSvg width={20} height={20} color="#D00D00" />
              </button>
            </Tooltip>
          </div>
        );
      },
    }),
    [t, isViewingDeleted, handleRestore, handleEditRow, handleDeleteRow]
  );

  const columns = useMemo(
    () => [...columnData, optionsColumn],
    [optionsColumn, columnData]
  );

  const CustomToolbar = useCallback(
    () => (
      <div className="flex flex-col items-start mt-2">
        <CustomGridToolbarQuickFilter fullWidth />

        <div>
          <CustomList>
            <ListItem>
              <Checkbox
                id="showDeletedLeaveTypes"
                checked={checkedItems.showDeletedLeaveTypes}
                onChange={handleToggleShowDeleted}
              />
              <ListItemText primary={t("leave.show-delete-leave-type")} />
            </ListItem>
          </CustomList>
        </div>
      </div>
    ),
    [t, checkedItems.showDeletedLeaveTypes, handleToggleShowDeleted]
  );

  useEffect(() => {
    setSelectedRow(null);
    setSelectionModel([]);
  }, [leaveTypesData]);


  return (
    <CustomDialog
      maxWidth={"xl"}
      open={props.open}
      onClose={props.handleClose}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">
              {t("common.configure-leave-type")}
            </p>
          </div>
          <GrayIconButton onClick={props.handleClose} className="self-start">
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="overflow-y-auto">
          <StyledDataGrid
            sx={{
              // đặt rowHeight nếu muốn chắc chắn
              "--DataGrid-rowHeight": "70px",
              // cao đúng 3 dòng + header => không hiện phân trang, chỉ cuộn
              height: "calc(var(--DataGrid-headerHeight) + (6 * var(--DataGrid-rowHeight)))",
              "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
              {
                display: "none !important",
              },

              "& .MuiDataGrid-scrollbarFiller": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                paddingRight: 0,
              },
              "& .MuiDataGrid-topContainer": {
                paddingRight: 0,
              },

              "&": {
                "--DataGrid-scrollbarSize": "0px",
              },

              "& .MuiDataGrid-virtualScroller": {
                scrollbarWidth: "none",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: 0,
                height: 0,
              },
              "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
              {
                scrollbarWidth: "thin",
              },
              "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
              {
                width: 10,
                height: 10,
              },

              '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                display: "none !important",
              },
            }}
            rows={rowData}
            columns={columns}
            getRowId={(row) => row.leaveTypeId ?? row.id}
            onRowClick={(params) => setSelectedRow(params.row)}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(newSelection) => {
              setSelectionModel(newSelection);
              const id = newSelection[0];
              const row = rowData.find(
                (r) => r.leaveTypeId === id || r.id === id
              );
              setSelectedRow(row || null);
            }}
            slots={{
              toolbar: CustomToolbar,
              // pagination: CustomPagination,
              loadingOverlay: LinearProgress,
              noRowsOverlay: NoRowOverlay,
              columnsPanel: CustomColumnsPanel,
            }}
            slotProps={{
              toolbar: { showQuickFilter: true },
            }}
            initialState={{
              filter: {
                filterModel: { items: [], quickFilterValues: [""] },
              },
            }}
            hideFooter
            hideFooterPagination
            hideFooterSelectedRowCount
            localeText={tableTranslated}
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={props.handleClose}>
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton onClick={handleOpenAddNewType}>
            <p>{t("button.new-leave-type")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>

      <AddNewLeaveTypeDialog
        open={openAddNewType}
        handleClose={handleCloseAddNewType}
        onRefresh={() => fetchLeaveTypeData(checkedItems.showDeletedLeaveTypes)}
        refreshData={props.onRefresh}
      />
      <EditLeaveTypeDialog
        open={openEditType}
        handleClose={handleCloseEditType}
        data={selectedRow}
        onRefresh={() => fetchLeaveTypeData(checkedItems.showDeletedLeaveTypes)}
        refreshData={props.onRefresh}
      />
      <DeleteLeaveTypeDialog
        open={openDeleteType}
        onClose={() => setOpenDeleteType(false)}
        leaveTypeInfo={selectedRow}
        onRefresh={() => fetchLeaveTypeData(checkedItems.showDeletedLeaveTypes)}
        refreshData={props.onRefresh}
      />
    </CustomDialog>
  );
};

export default ConfigureLeaveTypesDialog;